import type {
  OnboardingEligibility,
  OnboardingEligibilityStepKey,
  OnboardingStepCompletionFlags,
} from "./types";

/**
 * API step flags: `true` = still show / pending, `false` = skip (done).
 * UI completion flags: `true` = done.
 */
export function mapEligibilityToStepCompletion(
  data: OnboardingEligibility,
): OnboardingStepCompletionFlags {
  return {
    personal: !data.personalDetails,
    parent: !data.parentDetails,
    location: !data.locationDetails,
  };
}

/** First pending profile-setup step, or `"result"` when none left to show. */
export function getInitialOnboardingView(
  completion: OnboardingStepCompletionFlags,
): "personal" | "parent" | "location" | "result" {
  if (!completion.personal) return "personal";
  if (!completion.parent) return "parent";
  if (!completion.location) return "location";
  return "result";
}

/**
 * Next pending step after the given one (skips completed / false API flags).
 * `after` undefined = first pending (same as getInitialOnboardingView).
 */
export function getNextPendingProfileSetupView(
  completion: OnboardingStepCompletionFlags,
  after?: "personal" | "parent" | "location",
): "personal" | "parent" | "location" | "result" {
  const order = ["personal", "parent", "location"] as const;
  const startIdx = after ? order.indexOf(after) + 1 : 0;
  for (let i = startIdx; i < order.length; i++) {
    const key = order[i];
    if (!completion[key]) return key;
  }
  return "result";
}

export type DeeplinkOnboardingLanding =
  | "intro"
  | "personal"
  | "parent"
  | "location"
  | "dashboard";

const PAID_USER_ONBOARDING_INTRO_RESHOW_MS = 24 * 60 * 60 * 1000;

/**
 * Home/dashboard auto-open: `showIntroScreen`, or intro dismissed >24h ago (`updatedAt`).
 */
export function shouldAutoOpenPaidUserOnboardingIntro(
  data: OnboardingEligibility | null | undefined,
): boolean {
  if (!data) return false;
  if (data.showIntroScreen) return true;
  const updatedAtMs = Date.parse(data.updatedAt);
  if (!Number.isFinite(updatedAtMs)) return false;
  return Date.now() - updatedAtMs >= PAID_USER_ONBOARDING_INTRO_RESHOW_MS;
}

/**
 * Deeplink landing from eligibility:
 * 1) showIntroScreen → intro
 * 2) else first `true` among personal → parent → location
 * 3) else dashboard
 */
export function getDeeplinkOnboardingLanding(
  data: OnboardingEligibility,
): DeeplinkOnboardingLanding {
  if (data.showIntroScreen) return "intro";
  if (data.personalDetails) return "personal";
  if (data.parentDetails) return "parent";
  if (data.locationDetails) return "location";
  return "dashboard";
}

/**
 * After a profile-setup step is completed (not skipped): set that step flag to `false`
 * (no longer show). Skip must not call this.
 */
export function mergeEligibilityStep(
  current: OnboardingEligibility,
  step: OnboardingEligibilityStepKey,
  completed: boolean,
): OnboardingEligibility {
  return {
    ...current,
    [step]: completed,
    updatedAt: new Date().toISOString(),
  };
}

/** When user lands on the first profile-setup step (intro is done). */
export function markShowOnboardingFalse(
  current: OnboardingEligibility,
): OnboardingEligibility {
  return {
    ...current,
    showOnboarding: false,
    showIntroScreen: false,
    updatedAt: new Date().toISOString(),
  };
}

/** Only call when the paid-user intro screen is shown. */
export function markOnboardingIntroScreenVisible(
  current: OnboardingEligibility,
): OnboardingEligibility {
  return {
    ...current,
    showIntroScreen: true,
    updatedAt: new Date().toISOString(),
  };
}

/** Mark intro as dismissed so home no longer auto-opens paid-user onboarding. */
export function dismissOnboardingIntroScreen(
  current: OnboardingEligibility,
): OnboardingEligibility {
  return {
    ...current,
    showIntroScreen: false,
    updatedAt: new Date().toISOString(),
  };
}
